/**
 * components/needs/NeedItem.tsx
 *
 * Individual need item in the needs list editor.
 * Feb 2026: Added htmlFor/id label-input associations for accessibility.
 * Apr 2026: Converted grip handle div to button for native semantics; added aria-label to option remove button.
 * Apr 2026: Added type="button" and aria-label to main item remove button; corrected stale a11y comment.
 * Question-first paradigm (Feb 2026 refactor):
 * - Top row: admin label chip, type badge, condition pill (always visible), required/optional pill
 * - Editable question field with persistent label "Question affichée au client"
 * - Editable short_label field (optional, shown below question) for admin display in client detail
 * - Editable description field with persistent label conditionnel (document vs. information)
 * - Feb 2026: Removed document-specific inline info (quantity, subtypes)
 *   - information now expressed in example_description text
 *
 * Apr 2026: Major UX simplification:
 * - Removed gear icon / NeedItemConfigPanel - all config is now inline or pill-based
 * - FieldConditionDropdown always visible in top row (no toggle needed)
 * - Required/optional toggled via RequiredDropdown pill (direct click-to-edit)
 * - Choice field options (single_choice / multiple_choice) edited inline below description
 * Apr 2026 (UX polish):
 * - Labels contrast raised to text-neutral-500 for better readability (WCAG)
 * - Options show numbered index circles for order awareness
 * - Options support drag-and-drop reordering via HTML5 drag API + GripVertical handle
 * Apr 2026 (fix): dragKey counter incremented on each successful option reorder so React
 *   uses stable composite keys (dragKey-index) and avoids stale reconciliation after drag.
 * Apr 2026 (a11y): Added aria-label to option inputs; option drag handles are native button
 *   elements with Alt+ArrowUp/Down reordering support.
 * Apr 2026 (a11y fix): Restored keyboard focus on grip handle after Alt+Arrow reorder.
 *   dragKey remounts rows; focusAfterReorderRef stores the target index and a useEffect
 *   keyed on dragKey calls .focus() on the corresponding grip handle ref after remount.
 * Apr 2026 (fix): Switching type to 'info' now forces is_required to false in the same
 *   onUpdate call, keeping hidden-toggle state consistent with the underlying value.
 * Apr 2026 (system email field): when need.is_system is true, the item is
 *   displayed in a locked state - no drag handle, no type change, no remove button,
 *   no required toggle, no conditions. A lock badge and a non-interactive type chip
 *   are shown instead. The label and description remain editable.
 * Jun 2026: Added editable short_label field (Intitulé court, optionnel) shown in
 *   the client detail view in place of the full question. Displayed before the question field.
 *   Placeholder "Ex :" removed.
 */

import { useState, useEffect, useRef } from 'react';
import { GripVertical, X, Plus, Lock } from 'lucide-react';
import type { NeedItem as NeedItemType } from '../../types/needs';
import { FieldConditionDropdown } from './FieldConditionDropdown';
import FieldTypeDropdown from './FieldTypeDropdown';
import RequiredDropdown from './RequiredDropdown';

interface NeedItemProps {
  need: NeedItemType;
  availableConditionFields: NeedItemType[];
  onUpdate: (updates: Partial<NeedItemType>) => void;
  onRemove: () => void;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  isHighlighted?: boolean;
  onHighlightSource?: (fieldIds: string[]) => void;
  onClearHighlight?: () => void;
}

export default function NeedItem({
  need,
  availableConditionFields,
  onUpdate,
  onRemove,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isHighlighted = false,
  onHighlightSource,
  onClearHighlight,
}: NeedItemProps) {
  const [questionValue, setQuestionValue] = useState(need.label);
  const [shortLabelValue, setShortLabelValue] = useState(need.short_label || '');
  const [descriptionValue, setDescriptionValue] = useState(need.description || '');
  const [draggedOptionIndex, setDraggedOptionIndex] = useState<number | null>(null);
  const [dragOverOptionIndex, setDragOverOptionIndex] = useState<number | null>(null);
  const [dragKey, setDragKey] = useState(0);
  const gripHandleRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const focusAfterReorderRef = useRef<number | null>(null);

  useEffect(() => {
    setQuestionValue(need.label);
  }, [need.label]);

  useEffect(() => {
    setShortLabelValue(need.short_label || '');
  }, [need.short_label]);

  useEffect(() => {
    setDescriptionValue(need.description || '');
  }, [need.description]);

  useEffect(() => {
    if (focusAfterReorderRef.current !== null) {
      const target = gripHandleRefs.current[focusAfterReorderRef.current];
      focusAfterReorderRef.current = null;
      target?.focus();
    }
  }, [dragKey]);

  const handleQuestionBlur = () => {
    const trimmed = questionValue.trim();
    if (trimmed) onUpdate({ label: trimmed });
    else setQuestionValue(need.label);
  };

  const handleQuestionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = questionValue.trim();
      if (trimmed) onUpdate({ label: trimmed });
      else setQuestionValue(need.label);
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setQuestionValue(need.label);
      e.currentTarget.blur();
    }
  };

  const handleDescriptionBlur = () => {
    onUpdate({ description: descriptionValue.trim() || null });
  };

  const handleDescriptionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onUpdate({ description: descriptionValue.trim() || null });
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setDescriptionValue(need.description || '');
      e.currentTarget.blur();
    }
  };

  const handleShortLabelBlur = () => {
    onUpdate({ short_label: shortLabelValue.trim() || null });
  };

  const handleShortLabelKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onUpdate({ short_label: shortLabelValue.trim() || null });
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShortLabelValue(need.short_label || '');
      e.currentTarget.blur();
    }
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...(need.options || [''])];
    newOptions[index] = value;
    onUpdate({ options: newOptions });
  };

  const handleOptionRemove = (index: number) => {
    const newOptions = (need.options || []).filter((_, i) => i !== index);
    onUpdate({ options: newOptions });
  };

  const handleOptionAdd = () => {
    const newOptions = [...(need.options || ['']), ''];
    onUpdate({ options: newOptions });
  };

  const handleOptionDragStart = (e: React.DragEvent, index: number) => {
    e.stopPropagation();
    setDraggedOptionIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleOptionDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverOptionIndex(index);
  };

  const moveOption = (fromIndex: number, toIndex: number) => {
    const newOptions = [...(need.options || [''])];
    const dragged = newOptions[fromIndex];
    newOptions.splice(fromIndex, 1);
    newOptions.splice(toIndex, 0, dragged);
    onUpdate({ options: newOptions });
    setDragKey((k) => k + 1);
  };

  const handleOptionDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedOptionIndex === null || draggedOptionIndex === index) {
      setDraggedOptionIndex(null);
      setDragOverOptionIndex(null);
      return;
    }
    moveOption(draggedOptionIndex, index);
    setDraggedOptionIndex(null);
    setDragOverOptionIndex(null);
  };

  const handleOptionKeyboardReorder = (e: React.KeyboardEvent, index: number) => {
    const options = need.options || [''];
    if (e.altKey && e.key === 'ArrowUp' && index > 0) {
      e.preventDefault();
      focusAfterReorderRef.current = index - 1;
      moveOption(index, index - 1);
    } else if (e.altKey && e.key === 'ArrowDown' && index < options.length - 1) {
      e.preventDefault();
      focusAfterReorderRef.current = index + 1;
      moveOption(index, index + 1);
    }
  };

  const handleOptionDragEnd = (e: React.DragEvent) => {
    e.stopPropagation();
    setDraggedOptionIndex(null);
    setDragOverOptionIndex(null);
  };

  const isChoiceField = need.type === 'single_choice' || need.type === 'multiple_choice';
  const isInfoField = need.type === 'info';
  const isSystemField = need.is_system === true;

  return (
    <div
      draggable={!isSystemField}
      onDragStart={isSystemField ? undefined : onDragStart}
      onDragOver={isSystemField ? undefined : onDragOver}
      onDrop={isSystemField ? undefined : onDrop}
      onDragEnd={isSystemField ? undefined : onDragEnd}
      className={`group relative bg-white border rounded-xl p-4 transition-all ${
        isSystemField
          ? 'border-primary-200 bg-primary-50/30'
          : isHighlighted
          ? 'border-amber-400 ring-2 ring-amber-200 shadow-soft-lg hover:shadow-soft'
          : 'border-neutral-200 hover:shadow-soft'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Drag handle - spacer for system fields to preserve horizontal alignment */}
        {isSystemField ? (
          <div className="flex-shrink-0 w-5 pt-0.5" aria-hidden="true" />
        ) : (
          <div
            className="flex-shrink-0 cursor-move pt-0.5 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity text-neutral-400 hover:text-neutral-600"
            title="Glisser pour réordonner"
          >
            <GripVertical className="w-5 h-5" />
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Top row: type badge/dropdown → condition pill → required dropdown */}
          <div className="flex items-center gap-2 flex-wrap">
            {isSystemField ? (
              <>
                {/* System field: lock badge + static type chip */}
                <span className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-primary-100 text-primary-700 text-xs rounded-lg font-medium border border-primary-200">
                  <Lock className="w-3 h-3" />
                  Champ système
                </span>
                <span className="px-2.5 py-1.5 bg-neutral-100 text-neutral-500 text-xs rounded-lg font-medium border border-neutral-200">
                  Email
                </span>
                <span className="px-2 py-1.5 text-xs text-neutral-400 font-normal">
                  Obligatoire · Toujours en première position
                </span>
              </>
            ) : (
              <>
                <FieldTypeDropdown
                  value={need.type}
                  onChange={(newType) => onUpdate({ type: newType, is_required: newType === 'info' ? false : need.is_required })}
                  types={['document', 'short_text', 'text', 'date', 'single_choice', 'multiple_choice', 'info']}
                />
                <span
                  onMouseEnter={() => {
                    const sourceIds = (need.conditions || []).map((c) => c.source_field_id);
                    if (sourceIds.length > 0 && onHighlightSource) {
                      onHighlightSource(sourceIds);
                    }
                  }}
                  onMouseLeave={() => onClearHighlight?.()}
                >
                  <FieldConditionDropdown
                    conditions={need.conditions}
                    conditionOperator={need.condition_operator || 'or'}
                    availableFields={availableConditionFields}
                    onChange={(conditions) => onUpdate({ conditions })}
                    onOperatorChange={(condition_operator) => onUpdate({ condition_operator })}
                  />
                </span>
                {!isInfoField && (
                  <RequiredDropdown
                    isRequired={need.is_required}
                    onChange={(isRequired) => onUpdate({ is_required: isRequired })}
                  />
                )}
              </>
            )}
          </div>

          {/* Editable short_label — optional admin display label shown in client detail */}
          {!isInfoField && !isSystemField && (
            <div className="mt-2">
              <label htmlFor={`short-label-${need.id}`} className="block text-xs font-medium text-neutral-500 mb-0.5">
                Intitulé court{' '}
                <span className="font-normal text-neutral-400">(optionnel — affiché dans la fiche client)</span>
              </label>
              <input
                id={`short-label-${need.id}`}
                type="text"
                value={shortLabelValue}
                onChange={(e) => setShortLabelValue(e.target.value)}
                onBlur={handleShortLabelBlur}
                onKeyDown={handleShortLabelKeyDown}
                className="w-full text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded px-1.5 py-0.5 focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-600 focus:outline-none transition-colors"
              />
            </div>
          )}

          {/* Editable question / message title */}
          <div className="mt-1.5">
            <label htmlFor={`question-${need.id}`} className="block text-xs font-medium text-neutral-500 mb-0.5">
              {isInfoField ? 'Titre du message' : 'Question affichée au client'}
            </label>
            <input
              id={`question-${need.id}`}
              type="text"
              value={questionValue}
              onChange={(e) => setQuestionValue(e.target.value)}
              onBlur={handleQuestionBlur}
              onKeyDown={handleQuestionKeyDown}
              className="w-full text-sm font-medium text-neutral-900 bg-white border border-neutral-200 rounded px-1.5 py-0.5 focus:border-primary-300 focus:ring-2 focus:ring-primary-600 focus:outline-none transition-colors"
            />
          </div>

          {/* Editable description / example answer / message body */}
          <div className="mt-1.5">
            <label htmlFor={`description-${need.id}`} className="block text-xs font-medium text-neutral-500 mb-0.5">
              {isInfoField
                ? 'Corps du message'
                : need.type === 'document'
                  ? 'Exemple de document attendu'
                  : 'Exemple de réponse attendue'}{' '}
              {!isInfoField && <span className="font-normal text-neutral-400">(optionnel)</span>}
            </label>
            <input
              id={`description-${need.id}`}
              type="text"
              value={descriptionValue}
              onChange={(e) => setDescriptionValue(e.target.value)}
              onBlur={handleDescriptionBlur}
              onKeyDown={handleDescriptionKeyDown}
              className="w-full text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded px-1.5 py-0.5 focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-600 focus:outline-none transition-colors"
            />
          </div>

          {/* Inline options editor - only for single_choice / multiple_choice */}
          {isChoiceField && (
            <div className="mt-3 pt-2.5 border-t border-neutral-100">
              <p className="text-xs font-medium text-neutral-500 mb-1.5">Options de réponse</p>
              <div className="space-y-1.5">
                {(need.options || ['']).map((option, index) => (
                  <div
                    key={`${dragKey}-${index}`}
                    draggable
                    onDragStart={(e) => handleOptionDragStart(e, index)}
                    onDragOver={(e) => handleOptionDragOver(e, index)}
                    onDrop={(e) => handleOptionDrop(e, index)}
                    onDragEnd={handleOptionDragEnd}
                    className={`flex items-center gap-1.5 transition-opacity ${
                      draggedOptionIndex === index ? 'opacity-40' : 'opacity-100'
                    } ${
                      dragOverOptionIndex === index && draggedOptionIndex !== index
                        ? 'ring-2 ring-primary-300 rounded-lg'
                        : ''
                    }`}
                  >
                    <button
                      type="button"
                      ref={(el) => { gripHandleRefs.current[index] = el; }}
                      aria-label={`Réordonner l'option ${index + 1} (Alt+Flèche haut/bas)`}
                      className="flex-shrink-0 cursor-grab active:cursor-grabbing text-neutral-300 hover:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-1 rounded transition-colors bg-transparent border-0 p-0 m-0"
                      title="Glisser pour réordonner"
                      onKeyDown={(e) => handleOptionKeyboardReorder(e, index)}
                    >
                      <GripVertical className="w-3.5 h-3.5" />
                    </button>
                    <div className="w-5 h-5 rounded-full bg-neutral-100 border border-neutral-200 flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-semibold text-neutral-400 leading-none">
                        {index + 1}
                      </span>
                    </div>
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      aria-label={`Option ${index + 1}`}
                      className="flex-1 px-2 py-1 text-xs border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent bg-white text-neutral-700 outline-none transition-colors"
                    />
                    {(need.options?.length || 0) > 1 && (
                      <button
                        type="button"
                        onClick={() => handleOptionRemove(index)}
                        className="p-1 text-neutral-400 hover:text-error-500 hover:bg-error-50 rounded transition-colors flex-shrink-0"
                        title="Supprimer cette option"
                        aria-label={`Supprimer l'option ${index + 1}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleOptionAdd}
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium mt-0.5 transition-colors"
                >
                  <Plus className="w-3 h-3" />
                  Ajouter une option
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Remove button - spacer for system fields to preserve horizontal alignment */}
        {isSystemField ? (
          <div className="flex-shrink-0 w-8" aria-hidden="true" />
        ) : (
          <div className="flex-shrink-0 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={onRemove}
              className="p-2 text-error-500 hover:text-error-700 hover:bg-error-50 rounded-lg transition-colors"
              title="Supprimer"
              aria-label="Supprimer l'élément"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
